import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function ContactFormPage() {
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", mobile: "", whatsapp_number: "", email: "",
    city: "", state: "", address: "",
    contact_type: "lead", source: "walk-in", tags: "",
    birthday: "", anniversary: "",
    metals: "", styles: "", budget_min: "", budget_max: "", notes: "",
  });

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    const important_dates = [];
    if (f.birthday) important_dates.push({ label: "Birthday", date: new Date(f.birthday).toISOString() });
    if (f.anniversary) important_dates.push({ label: "Anniversary", date: new Date(f.anniversary).toISOString() });
    const payload = {
      name: f.name, mobile: f.mobile, whatsapp_number: f.whatsapp_number, email: f.email,
      city: f.city, state: f.state, address: f.address,
      contact_type: f.contact_type, source: f.source,
      tags: f.tags.split(",").map(x=>x.trim()).filter(Boolean),
      important_dates,
      preferences: {
        metals: f.metals.split(",").map(x=>x.trim()).filter(Boolean),
        styles: f.styles.split(",").map(x=>x.trim()).filter(Boolean),
        budget_min: f.budget_min === "" ? null : Number(f.budget_min),
        budget_max: f.budget_max === "" ? null : Number(f.budget_max),
        notes: f.notes,
      },
    };
    try {
      const { data } = await api.post("/contacts", payload);
      toast.success("Contact created");
      nav(`/contacts/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="contact-form-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">CRM</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">New Contact</h1>
      </div>

      <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Name*</Label><Input value={f.name} onChange={(e)=>setF({...f, name:e.target.value})} className="mt-1" data-testid="contact-name-input"/></div>
          <div><Label>Type</Label>
            <Select value={f.contact_type} onValueChange={(v)=>setF({...f, contact_type:v})}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="lead">Lead</SelectItem><SelectItem value="customer">Customer</SelectItem><SelectItem value="vendor">Vendor</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Mobile</Label><Input value={f.mobile} onChange={(e)=>setF({...f, mobile:e.target.value})} className="mt-1" data-testid="contact-mobile-input"/></div>
          <div><Label>WhatsApp</Label><Input value={f.whatsapp_number} onChange={(e)=>setF({...f, whatsapp_number:e.target.value})} className="mt-1"/></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e)=>setF({...f, email:e.target.value})} className="mt-1"/></div>
          <div><Label>Source</Label>
            <Select value={f.source} onValueChange={(v)=>setF({...f, source:v})}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{["walk-in","referral","whatsapp","catalog","instagram","other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>City</Label><Input value={f.city} onChange={(e)=>setF({...f, city:e.target.value})} className="mt-1"/></div>
          <div><Label>State</Label><Input value={f.state} onChange={(e)=>setF({...f, state:e.target.value})} className="mt-1"/></div>
        </div>
        <div><Label>Address</Label><Textarea rows={2} value={f.address} onChange={(e)=>setF({...f, address:e.target.value})} className="mt-1"/></div>
        <div><Label>Tags (comma-separated)</Label><Input value={f.tags} onChange={(e)=>setF({...f, tags:e.target.value})} placeholder="vip, bridal, diamond-lover" className="mt-1"/></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Birthday</Label><Input type="date" value={f.birthday} onChange={(e)=>setF({...f, birthday:e.target.value})} className="mt-1"/></div>
          <div><Label>Anniversary</Label><Input type="date" value={f.anniversary} onChange={(e)=>setF({...f, anniversary:e.target.value})} className="mt-1"/></div>
        </div>

        <div className="pt-4 border-t border-[#E4DFCF]">
          <div className="font-serif text-lg mb-2">Product Preferences</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Metals</Label><Input value={f.metals} onChange={(e)=>setF({...f, metals:e.target.value})} placeholder="gold, diamond" className="mt-1"/></div>
            <div><Label>Styles</Label><Input value={f.styles} onChange={(e)=>setF({...f, styles:e.target.value})} placeholder="temple, antique" className="mt-1"/></div>
            <div><Label>Budget min (₹)</Label><Input type="number" value={f.budget_min} onChange={(e)=>setF({...f, budget_min:e.target.value})} className="mt-1"/></div>
            <div><Label>Budget max (₹)</Label><Input type="number" value={f.budget_max} onChange={(e)=>setF({...f, budget_max:e.target.value})} className="mt-1"/></div>
          </div>
          <div className="mt-3"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e)=>setF({...f, notes:e.target.value})} className="mt-1"/></div>
        </div>

        <Button onClick={submit} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="save-contact-btn">Create Contact</Button>
      </CardContent></Card>
    </div>
  );
}
